import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Ban,
  Award,
  BadgeCheck,
  BarChart3,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Eye,
  Filter,
  Link2,
  Loader2,
  MapPin,
  Pencil,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Share2,
  Trash2,
  TrendingUp,
  Users,
  X,
  Zap,
} from 'lucide-react';
import {
  AnalyticsSection,
  AVAILABILITY_OPTIONS,
  CandidateDataRow,
  DataRow,
  DataShare,
  DataShareEvent,
  DataSearchRequest,
  FacetCount,
  FacetGroup,
  RecruiterDataRow,
  SHARE_EVENT_LABELS,
  ShareAudience,
  TalentAnalytics,
  TalentStats,
} from '../../Types/CandidateDataType';
import {
  createDataShare,
  deleteDataShare,
  getCandidateDataAnalytics,
  getCandidateDataStats,
  getDataShareEvents,
  getDataShares,
  onboardData,
  restoreDataShare,
  revokeDataShare,
  searchCandidateData,
  updateDataShare,
} from '../../Services/CandidateDataService';

interface Filters {
  q: string;
  city: string;
  role: string;
  availabilityStatus: string;
  roleLevel: string;
  domain: string;
  expertise: string;
  minExperience: string;
  maxExperience: string;
  fromDate: string;
  toDate: string;
  /** Generic analytics drill-down: attribute + value of the clicked distribution bar. */
  field: string;
  value: string;
}

const EMPTY_FILTERS: Filters = {
  q: '',
  city: '',
  role: '',
  availabilityStatus: '',
  roleLevel: '',
  domain: '',
  expertise: '',
  minExperience: '',
  maxExperience: '',
  fromDate: '',
  toDate: '',
  field: '',
  value: '',
};

const PAGE_SIZE = 20;

type ConsoleTab = 'CANDIDATE' | 'RECRUITER' | 'SHARES';

type ShareFilter = 'ALL' | 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'DELETED';

/** Tiles shown above the tracking list; also act as the status filter. */
const SHARE_FILTERS: { key: ShareFilter; label: string; accent: string }[] = [
  { key: 'ALL', label: 'All links', accent: 'from-cyan-500 to-teal-500' },
  { key: 'ACTIVE', label: 'Active', accent: 'from-emerald-500 to-teal-500' },
  { key: 'EXPIRED', label: 'Expired', accent: 'from-amber-500 to-orange-500' },
  { key: 'REVOKED', label: 'Revoked', accent: 'from-rose-500 to-pink-500' },
  { key: 'DELETED', label: 'Deleted', accent: 'from-slate-500 to-slate-600' },
];

/** Fixed order the per-link metric table lists every tracked event in. */
const SHARE_EVENT_ORDER: (keyof typeof SHARE_EVENT_LABELS)[] = [
  'LINK_CREATED',
  'LINK_SENT',
  'LINK_OPENED',
  'FILTERS_APPLIED',
  'RESULT_VIEWED',
  'EXPORTED',
  'ONBOARD_CLICKED',
  'ONBOARD_SENT',
];

/** Order the "Talent Insights" categories are shown in; unknown categories fall to the end. */
const CATEGORY_ORDER = [
  'Experience',
  'Who they are',
  'Availability',
  'Expertise',
  'Leadership & scope',
  'Track record',
  'Compensation',
  'Quality & trust',
  'Activity',
  'Recruiters',
  'Companies',
];

/** Rotating accent per category so the Insights wall stays colourful and scannable. */
const CATEGORY_COLORS = [
  'text-cyan-400',
  'text-violet-400',
  'text-emerald-400',
  'text-fuchsia-400',
  'text-sky-400',
  'text-amber-400',
  'text-rose-400',
  'text-teal-400',
  'text-indigo-400',
  'text-orange-400',
  'text-lime-400',
];

/** Rotating dot colour for the facet cards' headers. */
const FACET_DOTS = [
  'bg-cyan-400',
  'bg-violet-400',
  'bg-emerald-400',
  'bg-fuchsia-400',
  'bg-sky-400',
  'bg-amber-400',
  'bg-rose-400',
  'bg-teal-400',
];

/** Matching left-border accents for the Insights section cards. */
const CATEGORY_BORDERS = [
  'border-l-cyan-500/50',
  'border-l-violet-500/50',
  'border-l-emerald-500/50',
  'border-l-fuchsia-500/50',
  'border-l-sky-500/50',
  'border-l-amber-500/50',
  'border-l-rose-500/50',
  'border-l-teal-500/50',
  'border-l-indigo-500/50',
  'border-l-orange-500/50',
  'border-l-lime-500/50',
];

/** Bucket labels that represent missing data; they are shown but cannot drill into a filter. */
const UNKNOWN_BUCKETS = new Set(['Not specified', 'Not scored', 'Not set', 'Unknown', '']);

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Full date + time in 12-hour clock, e.g. "14 Oct 2026, 03:45:12 PM". */
const formatDateTime12h = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

/** Maps a backend experience facet label back onto the min/max filter pair. */
const parseExperienceBucket = (label: string): { min: string; max: string } => {
  const years = label.match(/\d+/g)?.map(Number) ?? [];
  if (label.includes('+')) return { min: String(years[0] ?? 10), max: '' };
  if (years.length >= 2) return { min: String(years[0]), max: String(years[1]) };
  return { min: '', max: '' };
};

/**
 * Admin console for the Candidate/Recruiter directory: filtered search with clickable facet counts,
 * expiring share links (mailed to recipients), per-share event tracking, and onboarding mails.
 *
 * <p>The candidate and recruiter views share one filter + share + tracking engine; the audience
 * toggle only swaps which columns and which facet groups are shown.</p>
 */
const CandidatesDataComponent: React.FC = () => {
  const [tab, setTab] = useState<ConsoleTab>('CANDIDATE');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);

  const [rows, setRows] = useState<DataRow[]>([]);
  const [facets, setFacets] = useState<FacetGroup[]>([]);
  const [stats, setStats] = useState<TalentStats | null>(null);
  const [analytics, setAnalytics] = useState<TalentAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const [shares, setShares] = useState<DataShare[]>([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [shareFilter, setShareFilter] = useState<ShareFilter>('ALL');

  const [editShare, setEditShare] = useState<DataShare | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    note: '',
    recipients: '',
    expiresInHours: 168,
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<DataShare | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const [shareOpen, setShareOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [shareForm, setShareForm] = useState({
    title: '',
    note: '',
    recipients: '',
    expiresInHours: 168,
  });

  const [onboardOpen, setOnboardOpen] = useState(false);
  const [onboardMsg, setOnboardMsg] = useState('');
  const [onboarding, setOnboarding] = useState(false);

  const [eventsShareId, setEventsShareId] = useState<string | null>(null);
  const [events, setEvents] = useState<DataShareEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsFrom, setEventsFrom] = useState('');
  const [eventsTo, setEventsTo] = useState('');

  const audience: ShareAudience = tab === 'RECRUITER' ? 'RECRUITER' : 'CANDIDATE';
  const isCandidate = audience === 'CANDIDATE';
  const showShares = tab === 'SHARES';

  const buildParams = useCallback(
    (): DataSearchRequest => ({
      audience,
      page,
      size: PAGE_SIZE,
      q: filters.q || undefined,
      city: filters.city || undefined,
      role: filters.role || undefined,
      availabilityStatus: filters.availabilityStatus || undefined,
      roleLevel: filters.roleLevel || undefined,
      domain: filters.domain || undefined,
      expertise: filters.expertise || undefined,
      minExperience: filters.minExperience ? Number(filters.minExperience) : null,
      maxExperience: filters.maxExperience ? Number(filters.maxExperience) : null,
      fromDate: filters.fromDate || undefined,
      toDate: filters.toDate || undefined,
      field: filters.field || undefined,
      value: filters.value || undefined,
    }),
    [audience, page, filters]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setAnalyticsLoading(true);
    setError(null);
    const params = buildParams();
    const [res, statsRes, analyticsRes] = await Promise.all([
      searchCandidateData(params),
      getCandidateDataStats(params),
      getCandidateDataAnalytics(params),
    ]);
    if (res.success && res.data) {
      setRows(res.data.content ?? []);
      setFacets(res.data.facets ?? []);
      setTotal(res.data.totalElements ?? 0);
      setTotalPages(res.data.totalPages ?? 0);
    } else {
      setError(res.message || 'Failed to load the directory');
      setRows([]);
      setFacets([]);
    }
    setStats(statsRes.success && statsRes.data ? statsRes.data : null);
    setAnalytics(analyticsRes.success && analyticsRes.data ? analyticsRes.data : null);
    setLoading(false);
    setAnalyticsLoading(false);
  }, [buildParams]);

  useEffect(() => {
    load();
  }, [load]);

  const loadShares = useCallback(async () => {
    setSharesLoading(true);
    const res = await getDataShares(true);
    if (res.success && res.data) {
      setShares(res.data);
    }
    setSharesLoading(false);
  }, []);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  const shareStatusOf = useCallback(
    (s: DataShare): ShareFilter => (s.deleted ? 'DELETED' : (s.status as ShareFilter)),
    []
  );

  const shareCounts = useMemo(() => {
    const counts: Record<ShareFilter, number> = {
      ALL: 0,
      ACTIVE: 0,
      EXPIRED: 0,
      REVOKED: 0,
      DELETED: 0,
    };
    shares.forEach((s) => {
      if (s.deleted) {
        counts.DELETED += 1;
        return;
      }
      counts.ALL += 1;
      counts[s.status] = (counts[s.status] ?? 0) + 1;
    });
    return counts;
  }, [shares]);

  const visibleShares = useMemo(() => {
    if (shareFilter === 'ALL') {
      return shares.filter((s) => !s.deleted);
    }
    return shares.filter((s) => shareStatusOf(s) === shareFilter);
  }, [shares, shareFilter, shareStatusOf]);

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((id) => selected[id]),
    [selected]
  );

  const setFilter = (patch: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  };

  const applyFacet = (group: FacetGroup, value: string) => {
    setFilters((prev) => ({ ...prev, field: '', value: '' }));
    if (group.key === 'city') setFilter({ city: value });
    else if (group.key === 'role') setFilter({ role: value });
    else if (group.key === 'availabilityStatus') setFilter({ availabilityStatus: value });
    else if (group.key === 'experience') {
      const bucket = parseExperienceBucket(value);
      setFilter({ minExperience: bucket.min, maxExperience: bucket.max });
    }
  };

  /**
   * Drill into a "Talent Insights" bar. Experience buckets map onto the min/max filter pair; every
   * other clickable section uses the generic {@code field}/{@code value} filter so new analytics
   * cards become interactive without a frontend change.
   */
  const applyAnalyticsFilter = (section: AnalyticsSection, value: string) => {
    if (section.key === 'experience') {
      const bucket = parseExperienceBucket(value);
      if (bucket.min === '' && bucket.max === '') {
        return;
      }
      setFilters((prev) => ({
        ...prev,
        field: '',
        value: '',
        minExperience: bucket.min,
        maxExperience: bucket.max,
      }));
      setPage(0);
      return;
    }
    if (!section.filterField) {
      return;
    }
    setFilters((prev) => ({
      ...prev,
      city: '',
      role: '',
      availabilityStatus: '',
      roleLevel: '',
      domain: '',
      expertise: '',
      minExperience: '',
      maxExperience: '',
      field: section.filterField as string,
      value,
    }));
    setPage(0);
  };

  const switchTab = (next: ConsoleTab) => {
    setTab(next);
    setFilters(EMPTY_FILTERS);
    setPage(0);
    setSelected({});
  };

  const toggleRow = (id: string) =>
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleAllOnPage = () => {
    const allSelected = rows.length > 0 && rows.every((r) => selected[r.id]);
    const next: Record<string, boolean> = { ...selected };
    rows.forEach((r) => {
      next[r.id] = !allSelected;
    });
    setSelected(next);
  };

  const copyText = (text: string) => {
    navigator.clipboard?.writeText(text);
    setBanner('Link copied to clipboard');
  };

  const submitShare = async () => {
    setCreating(true);
    setError(null);
    const recipients = shareForm.recipients
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const res = await createDataShare({
      audience,
      title: shareForm.title || undefined,
      note: shareForm.note || undefined,
      filters: { ...buildParams(), page: 0 },
      recipientEmails: recipients,
      expiresInHours: shareForm.expiresInHours || null,
    });
    if (res.success && res.data) {
      setShareOpen(false);
      setShareForm({ title: '', note: '', recipients: '', expiresInHours: 168 });
      setBanner(`Share link ready: ${res.data.url}`);
      loadShares();
    } else {
      setError(res.message || 'Failed to create the share');
    }
    setCreating(false);
  };

  const submitOnboard = async () => {
    setOnboarding(true);
    setError(null);
    const res = await onboardData({
      audience,
      ids: selectedIds,
      message: onboardMsg || null,
    });
    if (res.success && res.data) {
      setBanner(`Onboarding sent to ${res.data.sent} ${isCandidate ? 'candidates' : 'recruiters'}`);
      setOnboardOpen(false);
      setOnboardMsg('');
      setSelected({});
    } else {
      setError(res.message || 'Failed to send onboarding');
    }
    setOnboarding(false);
  };

  const revoke = async (share: DataShare) => {
    const res = await revokeDataShare(share.id);
    if (res.success) {
      loadShares();
    }
  };

  const remainingHours = (iso: string | null): number => {
    if (!iso) {
      return 168;
    }
    const diff = new Date(iso).getTime() - Date.now();
    if (diff <= 0) {
      return 1;
    }
    return Math.max(1, Math.round(diff / 3600000));
  };

  const openEdit = (share: DataShare) => {
    setEditShare(share);
    setEditForm({
      title: share.title ?? '',
      note: share.note ?? '',
      recipients: share.recipientEmails.join(', '),
      expiresInHours: remainingHours(share.expiresAt),
    });
  };

  const submitEdit = async () => {
    if (!editShare) {
      return;
    }
    setSavingEdit(true);
    setError(null);
    const recipients = editForm.recipients
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const res = await updateDataShare(editShare.id, {
      title: editForm.title,
      note: editForm.note,
      recipientEmails: recipients,
      expiresInHours: editForm.expiresInHours || null,
    });
    if (res.success) {
      setEditShare(null);
      setBanner('Share link updated');
      loadShares();
    } else {
      setError(res.message || 'Failed to update the share');
    }
    setSavingEdit(false);
  };

  const confirmDeleteShare = async () => {
    if (!confirmDelete) {
      return;
    }
    setDeletingId(confirmDelete.id);
    setError(null);
    const res = await deleteDataShare(confirmDelete.id);
    if (res.success) {
      setBanner('Share link moved to Deleted');
      setConfirmDelete(null);
      loadShares();
    } else {
      setError(res.message || 'Failed to delete the share');
    }
    setDeletingId(null);
  };

  const restore = async (share: DataShare) => {
    setRestoringId(share.id);
    setError(null);
    const res = await restoreDataShare(share.id);
    if (res.success) {
      setBanner('Share link restored');
      loadShares();
    } else {
      setError(res.message || 'Failed to restore the share');
    }
    setRestoringId(null);
  };

  const toggleEvents = async (share: DataShare) => {
    if (eventsShareId === share.id) {
      setEventsShareId(null);
      setEvents([]);
      return;
    }
    setEventsShareId(share.id);
    setEventsFrom('');
    setEventsTo('');
    setEventsLoading(true);
    const res = await getDataShareEvents(share.id);
    if (res.success && res.data) {
      setEvents(res.data);
    } else {
      setEvents([]);
    }
    setEventsLoading(false);
  };

  const filteredEvents = useMemo(() => {
    if (!eventsFrom && !eventsTo) {
      return events;
    }
    const from = eventsFrom ? new Date(`${eventsFrom}T00:00:00`).getTime() : null;
    const to = eventsTo ? new Date(`${eventsTo}T23:59:59.999`).getTime() : null;
    return events.filter((e) => {
      if (!e.createdAt) {
        return false;
      }
      const t = new Date(e.createdAt).getTime();
      if (Number.isNaN(t)) {
        return false;
      }
      if (from != null && t < from) {
        return false;
      }
      if (to != null && t > to) {
        return false;
      }
      return true;
    });
  }, [events, eventsFrom, eventsTo]);

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/20 via-sky-500/10 to-violet-500/20 text-cyan-300">
            <Users size={22} />
          </span>
          <div>
            <h1 className="bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-300 bg-clip-text text-xl font-black tracking-tight text-transparent md:text-2xl">
              Candidates Data
            </h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Search the directory, share filtered lists, track opens and onboard
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border border-slate-800 bg-slate-900/60 p-1">
            {([
              { key: 'CANDIDATE', label: 'Candidates' },
              { key: 'RECRUITER', label: 'Recruiters' },
              { key: 'SHARES', label: 'Shares & tracking' },
            ] as { key: ConsoleTab; label: string }[]).map((t) => (
              <button
                key={t.key}
                onClick={() => switchTab(t.key)}
                className={`rounded-lg px-3.5 py-2 text-xs font-black uppercase tracking-wider transition ${
                  tab === t.key
                    ? t.key === 'CANDIDATE'
                      ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950'
                      : t.key === 'RECRUITER'
                        ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-slate-950'
                        : 'bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950'
                    : 'text-slate-400 hover:text-cyan-400'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-slate-950 transition hover:opacity-90"
          >
            <Share2 size={15} /> Share
          </button>
        </div>
      </div>

      {banner && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-300">
          <span className="break-all">{banner}</span>
          <button onClick={() => setBanner(null)}>
            <X size={16} />
          </button>
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-300">
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X size={16} />
          </button>
        </div>
      )}

      {showShares ? (
        <section className="rounded-2xl border border-t-2 border-slate-800 border-t-fuchsia-500/50 bg-slate-900/60 p-4">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-400">
              <Link2 size={14} className="text-fuchsia-400" /> Shares &amp; tracking
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
              {visibleShares.length} {visibleShares.length === 1 ? 'link' : 'links'}
            </span>
          </div>

          {/* Counts — clicking a tile filters the list */}
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {SHARE_FILTERS.map((f) => {
              const active = shareFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setShareFilter(f.key)}
                  className={`overflow-hidden rounded-xl border text-left transition ${
                    active
                      ? 'border-slate-600 bg-slate-800/70'
                      : 'border-slate-800 bg-slate-950/50 hover:border-slate-700'
                  }`}
                >
                  <div className={`h-1 w-full bg-gradient-to-r ${f.accent}`} />
                  <div className="p-2.5">
                    <p className="text-xl font-black text-slate-100">{shareCounts[f.key]}</p>
                    <p
                      className={`text-[10px] font-bold uppercase tracking-wider ${
                        active ? 'text-slate-200' : 'text-slate-500'
                      }`}
                    >
                      {f.label}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {sharesLoading ? (
            <Loader2 className="animate-spin text-slate-500" size={18} />
          ) : visibleShares.length === 0 ? (
            <p className="text-[11px] font-semibold text-slate-600">
              {shareFilter === 'DELETED'
                ? 'No deleted links.'
                : 'No shares here yet. Use “Share” to send a filtered list.'}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {visibleShares.map((share) => (
                <div key={share.id} className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-100">
                        {share.title || 'Untitled share'}
                        <span className="ml-2 rounded-md bg-slate-800 px-1.5 py-0.5 text-[10px] font-black uppercase text-slate-400">
                          {share.audience}
                        </span>
                        <span
                          className={`ml-2 rounded-md px-1.5 py-0.5 text-[10px] font-black uppercase ${
                            share.deleted
                              ? 'bg-slate-500/15 text-slate-300'
                              : share.status === 'ACTIVE'
                                ? 'bg-emerald-500/15 text-emerald-300'
                                : share.status === 'EXPIRED'
                                  ? 'bg-amber-500/15 text-amber-300'
                                  : 'bg-rose-500/15 text-rose-300'
                          }`}
                        >
                          {share.deleted ? 'DELETED' : share.status}
                        </span>
                      </p>
                      <p className="mt-0.5 truncate text-[11px] font-semibold text-slate-500">
                        {share.url}
                      </p>
                      {share.expiresAt && (
                        <p className="mt-0.5 text-[10px] font-semibold text-slate-600">
                          Expires {formatDate(share.expiresAt)}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <button
                        onClick={() => copyText(share.url)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-800 px-2 py-1 text-[10px] font-black uppercase text-slate-400 hover:text-cyan-300"
                      >
                        <Copy size={12} /> Copy
                      </button>
                      {!share.deleted && (
                        <a
                          href={share.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-800 px-2 py-1 text-[10px] font-black uppercase text-slate-400 hover:text-cyan-300"
                        >
                          <Eye size={12} /> Preview
                        </a>
                      )}
                      <button
                        onClick={() => toggleEvents(share)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-800 px-2 py-1 text-[10px] font-black uppercase text-slate-400 hover:text-cyan-300"
                      >
                        <Clock size={12} /> Events
                      </button>
                      {share.deleted ? (
                        <button
                          onClick={() => restore(share)}
                          disabled={restoringId === share.id}
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/30 px-2 py-1 text-[10px] font-black uppercase text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
                        >
                          {restoringId === share.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <RotateCcw size={12} />
                          )}{' '}
                          Restore
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => openEdit(share)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-800 px-2 py-1 text-[10px] font-black uppercase text-slate-400 hover:text-cyan-300"
                          >
                            <Pencil size={12} /> Edit
                          </button>
                          {share.status === 'ACTIVE' && (
                            <button
                              onClick={() => revoke(share)}
                              className="inline-flex items-center gap-1 rounded-lg border border-rose-500/30 px-2 py-1 text-[10px] font-black uppercase text-rose-300 hover:bg-rose-500/10"
                            >
                              <Ban size={12} /> Revoke
                            </button>
                          )}
                          <button
                            onClick={() => setConfirmDelete(share)}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-500/30 px-2 py-1 text-[10px] font-black uppercase text-rose-300 hover:bg-rose-500/10"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Per-link metric counts */}
                  <div className="mt-3 overflow-x-auto rounded-lg border border-slate-800">
                    <table className="w-full text-left text-[11px]">
                      <thead>
                        <tr className="border-b border-slate-800 bg-slate-900/50 text-[10px] uppercase tracking-wider text-slate-500">
                          <th className="whitespace-nowrap px-2.5 py-1.5 font-black">
                            <span className="inline-flex items-center gap-1.5">
                              <Eye size={12} className="text-slate-500" /> Views
                            </span>
                          </th>
                          {SHARE_EVENT_ORDER.map((type) => (
                            <th key={type} className="whitespace-nowrap px-2.5 py-1.5 font-black">
                              {SHARE_EVENT_LABELS[type]}
                            </th>
                          ))}
                          <th className="whitespace-nowrap px-2.5 py-1.5 font-black">Total events</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="px-2.5 py-1.5 font-black text-slate-100">
                            {share.viewCount.toLocaleString()}
                          </td>
                          {SHARE_EVENT_ORDER.map((type) => (
                            <td key={type} className="px-2.5 py-1.5 font-black text-slate-300">
                              {(share.eventCounts[type] ?? 0).toLocaleString()}
                            </td>
                          ))}
                          <td className="px-2.5 py-1.5 font-black text-cyan-300">
                            {share.totalEvents.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {eventsShareId === share.id && (
                    <div className="mt-3 border-t border-slate-800 pt-3">
                      {eventsLoading ? (
                        <Loader2 className="animate-spin text-slate-500" size={16} />
                      ) : (
                        <>
                          <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
                            <div className="flex flex-wrap items-end gap-2">
                              <label className="block">
                                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                  From
                                </span>
                                <input
                                  type="date"
                                  value={eventsFrom}
                                  onChange={(e) => setEventsFrom(e.target.value)}
                                  className="rounded-lg border border-slate-800 bg-slate-950/60 px-2 py-1.5 text-xs text-slate-100 outline-none"
                                />
                              </label>
                              <label className="block">
                                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                  To
                                </span>
                                <input
                                  type="date"
                                  value={eventsTo}
                                  onChange={(e) => setEventsTo(e.target.value)}
                                  className="rounded-lg border border-slate-800 bg-slate-950/60 px-2 py-1.5 text-xs text-slate-100 outline-none"
                                />
                              </label>
                              {(eventsFrom || eventsTo) && (
                                <button
                                  onClick={() => {
                                    setEventsFrom('');
                                    setEventsTo('');
                                  }}
                                  className="rounded-lg border border-slate-800 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400 hover:text-slate-200"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
                              {filteredEvents.length} of {events.length} events
                            </span>
                          </div>

                          {filteredEvents.length === 0 ? (
                            <p className="text-[11px] font-semibold text-slate-600">
                              No events in this range.
                            </p>
                          ) : (
                            <div className="overflow-x-auto rounded-lg border border-slate-800">
                              <table className="w-full text-left text-[11px]">
                                <thead>
                                  <tr className="border-b border-slate-800 bg-slate-900/50 text-[10px] uppercase tracking-wider text-slate-500">
                                    <th className="px-2.5 py-1.5 font-black">Event</th>
                                    <th className="px-2.5 py-1.5 font-black">Actor</th>
                                    <th className="px-2.5 py-1.5 font-black">Details</th>
                                    <th className="px-2.5 py-1.5 font-black">IP</th>
                                    <th className="whitespace-nowrap px-2.5 py-1.5 font-black">
                                      Date &amp; time
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredEvents.map((e) => (
                                    <tr key={e.id} className="border-b border-slate-800/60 last:border-0">
                                      <td className="whitespace-nowrap px-2.5 py-1.5 font-semibold text-slate-300">
                                        <span className="inline-flex items-center gap-1.5">
                                          <CheckCircle2 size={12} className="text-cyan-400" />
                                          {SHARE_EVENT_LABELS[e.eventType] ?? e.eventType}
                                        </span>
                                      </td>
                                      <td className="px-2.5 py-1.5">
                                        {e.actorType ? (
                                          <span className="rounded bg-slate-800 px-1.5 text-[9px] font-black uppercase text-slate-400">
                                            {e.actorType}
                                          </span>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                      <td
                                        className="max-w-[220px] truncate px-2.5 py-1.5 font-semibold text-slate-500"
                                        title={e.metadata || e.filtersJson || undefined}
                                      >
                                        {e.metadata || e.filtersJson || '—'}
                                      </td>
                                      <td className="px-2.5 py-1.5 font-semibold text-slate-500">
                                        {e.ipAddress ?? '—'}
                                      </td>
                                      <td className="whitespace-nowrap px-2.5 py-1.5 font-semibold text-slate-400">
                                        {formatDateTime12h(e.createdAt)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      ) : (
        <>
          {/* Filters — full-width horizontal bar */}
          <div className="rounded-2xl border border-t-2 border-slate-800 border-t-cyan-500/50 bg-slate-900/60 p-4">
            <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-400">
              <Filter size={14} className="text-cyan-400" /> Filters
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              <label className="col-span-2 block">
                <span className="mb-1 block text-[11px] font-bold text-slate-500">Search</span>
                <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/60 px-3">
                  <Search size={14} className="text-slate-500" />
                  <input
                    value={filters.q}
                    onChange={(e) => setFilter({ q: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') load();
                    }}
                    placeholder="Name, email, role, company"
                    className="w-full bg-transparent py-2 text-sm text-slate-100 outline-none"
                  />
                </div>
              </label>

              {isCandidate && (
                <>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-bold text-slate-500">City</span>
                    <input
                      value={filters.city}
                      onChange={(e) => setFilter({ city: e.target.value })}
                      className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-bold text-slate-500">Role</span>
                    <input
                      value={filters.role}
                      onChange={(e) => setFilter({ role: e.target.value })}
                      className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-bold text-slate-500">Role level</span>
                    <input
                      value={filters.roleLevel}
                      onChange={(e) => setFilter({ roleLevel: e.target.value })}
                      className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-bold text-slate-500">Availability</span>
                    <select
                      value={filters.availabilityStatus}
                      onChange={(e) => setFilter({ availabilityStatus: e.target.value })}
                      className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
                    >
                      <option value="">Any</option>
                      {AVAILABILITY_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-bold text-slate-500">Min exp</span>
                    <input
                      type="number"
                      value={filters.minExperience}
                      onChange={(e) => setFilter({ minExperience: e.target.value })}
                      className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] font-bold text-slate-500">Max exp</span>
                    <input
                      type="number"
                      value={filters.maxExperience}
                      onChange={(e) => setFilter({ maxExperience: e.target.value })}
                      className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
                    />
                  </label>
                </>
              )}

              <label className="block">
                <span className="mb-1 block text-[11px] font-bold text-slate-500">From</span>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => setFilter({ fromDate: e.target.value })}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-2 py-2 text-xs text-slate-100 outline-none"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold text-slate-500">To</span>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => setFilter({ toDate: e.target.value })}
                  className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-2 py-2 text-xs text-slate-100 outline-none"
                />
              </label>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-800 pt-3">
              <button
                onClick={load}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-5 py-2 text-xs font-black uppercase tracking-wider text-cyan-300"
              >
                <RefreshCw size={14} /> Apply
              </button>
              <button
                onClick={() => {
                  setFilters(EMPTY_FILTERS);
                  setPage(0);
                }}
                className="rounded-lg border border-slate-800 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-400 hover:text-slate-200"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Facet counts — full-width row */}
          {facets.length > 0 && (
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {facets.map((group, i) => (
                <div key={group.key} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
                  <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-400">
                    <span className={`h-2 w-2 rounded-full ${FACET_DOTS[i % FACET_DOTS.length]}`} />
                    {group.label}
                  </div>
                  {group.values.length === 0 ? (
                    <p className="text-[11px] font-semibold text-slate-600">No values</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {group.values.map((v) => (
                        <button
                          key={v.value}
                          onClick={() => applyFacet(group, v.value)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950/60 px-2.5 py-1.5 text-[11px] font-semibold text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-300"
                        >
                          {v.value}
                          <span className="rounded-md bg-slate-800 px-1.5 text-[10px] font-black text-slate-300">
                            {v.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Results — full width */}
          <main className="mt-5 space-y-4">
          {stats && (
            <section className="rounded-2xl border border-t-2 border-slate-800 border-t-emerald-500/50 bg-slate-900/60 p-4">
              <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-400">
                <TrendingUp size={14} className="text-emerald-400" /> Talent density
                <span className="ml-auto text-[10px] font-bold normal-case text-slate-600">
                  scoped to current filters
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatTile
                  icon={<Users size={15} />}
                  label={isCandidate ? 'In pool' : 'Recruiters'}
                  value={stats.total}
                />
                {isCandidate ? (
                  <>
                    <StatTile
                      icon={<Award size={15} />}
                      label="10+ years"
                      value={stats.seniorCandidates}
                      accent="emerald"
                    />
                    <StatTile
                      icon={<Zap size={15} />}
                      label="Actively exploring"
                      value={stats.activelyExploring}
                      accent="amber"
                    />
                    <StatTile
                      icon={<BadgeCheck size={15} />}
                      label="Verified"
                      value={stats.verified}
                      accent="sky"
                    />
                  </>
                ) : (
                  <StatTile
                    icon={<BadgeCheck size={15} />}
                    label="Active"
                    value={stats.verified}
                    accent="emerald"
                  />
                )}
              </div>

              {isCandidate && (
                <>
                  <div className="mt-4">
                    <div className="mb-1 flex items-center justify-between text-[11px] font-bold text-slate-500">
                      <span>Seniority bar</span>
                      <span>
                        {stats.seniorCandidates.toLocaleString()} of {stats.total.toLocaleString()} clear 10 years
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500"
                        style={{
                          width: `${stats.total > 0 ? Math.round((stats.seniorCandidates / stats.total) * 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <MiniDist
                      title="Role level"
                      values={stats.byRoleLevel}
                      activeValue={filters.roleLevel}
                      onPick={(v) => setFilter({ roleLevel: v })}
                      accent="from-violet-500 to-fuchsia-500"
                    />
                    <MiniDist
                      title="Experience"
                      values={stats.byExperience}
                      onPick={(v) => {
                        const bucket = parseExperienceBucket(v);
                        setFilter({ minExperience: bucket.min, maxExperience: bucket.max });
                      }}
                      accent="from-cyan-500 to-teal-500"
                    />
                    <MiniDist
                      title="Top cities"
                      values={stats.byCity}
                      activeValue={filters.city}
                      onPick={(v) => setFilter({ city: v })}
                      accent="from-sky-500 to-cyan-500"
                    />
                    <MiniDist
                      title="Domain"
                      values={stats.byDomain}
                      activeValue={filters.domain}
                      onPick={(v) => setFilter({ domain: v })}
                      accent="from-emerald-500 to-teal-500"
                    />
                  </div>
                </>
              )}
            </section>
          )}

          <TalentInsights
            analytics={analytics}
            loading={analyticsLoading}
            activeField={filters.field}
            activeValue={filters.value}
            activeMin={filters.minExperience}
            activeMax={filters.maxExperience}
            onPick={applyAnalyticsFilter}
          />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-semibold text-slate-400">
              {loading ? 'Loading…' : `${total} ${isCandidate ? 'candidates' : 'recruiters'}`}
            </p>
            <button
              disabled={selectedIds.length === 0}
              onClick={() => setOnboardOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2 text-xs font-black uppercase tracking-wider text-emerald-300 transition disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Send size={14} /> Onboard{selectedIds.length ? ` (${selectedIds.length})` : ''}
            </button>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-gradient-to-r from-cyan-500/10 via-slate-900/0 to-violet-500/10 text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && rows.every((r) => selected[r.id])}
                      onChange={toggleAllOnPage}
                    />
                  </th>
                  <th className="px-3 py-3">Name</th>
                  {isCandidate ? (
                    <>
                      <th className="px-3 py-3">City</th>
                      <th className="px-3 py-3">Role</th>
                      <th className="px-3 py-3">Experience</th>
                      <th className="px-3 py-3">Availability</th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-3">Designation</th>
                      <th className="px-3 py-3">Company</th>
                      <th className="px-3 py-3">Status</th>
                    </>
                  )}
                  <th className="px-3 py-3">Joined</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-10 text-center text-slate-500">
                      <Loader2 className="mx-auto animate-spin" size={20} />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-10 text-center text-sm font-semibold text-slate-500">
                      No results for the current filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) =>
                    isCandidate ? (
                      <CandidateRow
                        key={row.id}
                        row={row as CandidateDataRow}
                        selected={!!selected[row.id]}
                        onToggle={toggleRow}
                      />
                    ) : (
                      <RecruiterRow
                        key={row.id}
                        row={row as RecruiterDataRow}
                        selected={!!selected[row.id]}
                        onToggle={toggleRow}
                      />
                    )
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-3">
            <button
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 px-3 py-2 text-xs font-bold text-slate-400 transition disabled:opacity-40"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span className="text-xs font-semibold text-slate-500">
              Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
            </span>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 px-3 py-2 text-xs font-bold text-slate-400 transition disabled:opacity-40"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </main>
        </>
      )}

      {/* Share modal */}
      {shareOpen && (
        <Modal title="Share this list" onClose={() => setShareOpen(false)}>
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">Title</span>
              <input
                value={shareForm.title}
                onChange={(e) => setShareForm({ ...shareForm, title: e.target.value })}
                placeholder={`${isCandidate ? 'Candidate' : 'Recruiter'} directory`}
                className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">Note to recipient</span>
              <textarea
                value={shareForm.note}
                onChange={(e) => setShareForm({ ...shareForm, note: e.target.value })}
                rows={3}
                className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">
                Recipients (comma separated)
              </span>
              <textarea
                value={shareForm.recipients}
                onChange={(e) => setShareForm({ ...shareForm, recipients: e.target.value })}
                rows={2}
                placeholder="someone@example.com"
                className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">Expires in (hours)</span>
              <input
                type="number"
                value={shareForm.expiresInHours}
                onChange={(e) =>
                  setShareForm({ ...shareForm, expiresInHours: Number(e.target.value) })
                }
                className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setShareOpen(false)}
                className="rounded-lg border border-slate-800 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-400"
              >
                Cancel
              </button>
              <button
                disabled={creating}
                onClick={submitShare}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-950 disabled:opacity-50"
              >
                {creating && <Loader2 size={14} className="animate-spin" />} Create &amp; send
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit share modal */}
      {editShare && (
        <Modal title="Edit share link" onClose={() => setEditShare(null)}>
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">Title</span>
              <input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">Note to recipient</span>
              <textarea
                value={editForm.note}
                onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                rows={3}
                className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">
                Recipients (comma separated)
              </span>
              <textarea
                value={editForm.recipients}
                onChange={(e) => setEditForm({ ...editForm, recipients: e.target.value })}
                rows={2}
                placeholder="someone@example.com"
                className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">
                Renew expiry in (hours from now)
              </span>
              <input
                type="number"
                value={editForm.expiresInHours}
                onChange={(e) => setEditForm({ ...editForm, expiresInHours: Number(e.target.value) })}
                className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setEditShare(null)}
                className="rounded-lg border border-slate-800 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-400"
              >
                Cancel
              </button>
              <button
                disabled={savingEdit}
                onClick={submitEdit}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-950 disabled:opacity-50"
              >
                {savingEdit && <Loader2 size={14} className="animate-spin" />} Save changes
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Soft-delete confirmation */}
      {confirmDelete && (
        <Modal title="Delete share link" onClose={() => setConfirmDelete(null)}>
          <div className="space-y-4">
            <p className="text-sm font-semibold text-slate-300">
              Move “{confirmDelete.title || 'Untitled share'}” to Deleted? The link stops working for
              recipients immediately, but you can restore it later from the Deleted view.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="rounded-lg border border-slate-800 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-400"
              >
                Cancel
              </button>
              <button
                disabled={deletingId === confirmDelete.id}
                onClick={confirmDeleteShare}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-rose-500 to-pink-500 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-950 disabled:opacity-50"
              >
                {deletingId === confirmDelete.id && <Loader2 size={14} className="animate-spin" />} Delete
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Onboard modal */}
      {onboardOpen && (
        <Modal title="Send onboarding" onClose={() => setOnboardOpen(false)}>
          <div className="space-y-3">
            <p className="text-xs font-semibold text-slate-400">
              {selectedIds.length} {isCandidate ? 'candidate(s)' : 'recruiter(s)'} selected.
            </p>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold text-slate-500">Message (optional)</span>
              <textarea
                value={onboardMsg}
                onChange={(e) => setOnboardMsg(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 outline-none"
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setOnboardOpen(false)}
                className="rounded-lg border border-slate-800 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-400"
              >
                Cancel
              </button>
              <button
                disabled={onboarding || selectedIds.length === 0}
                onClick={submitOnboard}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2 text-xs font-black uppercase tracking-wider text-slate-950 disabled:opacity-50"
              >
                {onboarding && <Loader2 size={14} className="animate-spin" />} Send
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ==========================================================================
   Row renderers
   ========================================================================== */

const CandidateRow = ({
  row,
  selected,
  onToggle,
}: {
  row: CandidateDataRow;
  selected: boolean;
  onToggle: (id: string) => void;
}) => (
  <tr className="border-b border-slate-800/60 hover:bg-slate-800/30">
    <td className="px-3 py-3">
      <input type="checkbox" checked={selected} onChange={() => onToggle(row.id)} />
    </td>
    <td className="px-3 py-3">
      <p className="font-bold text-slate-100">{row.fullName}</p>
      <p className="text-[11px] font-semibold text-slate-500">{row.email ?? '—'}</p>
    </td>
    <td className="px-3 py-3 text-slate-300">
      <span className="inline-flex items-center gap-1.5">
        <MapPin size={12} className="text-slate-600" />
        {row.city ?? '—'}
      </span>
    </td>
    <td className="px-3 py-3 text-slate-300">
      <span className="inline-flex items-center gap-1.5">
        <Briefcase size={12} className="text-slate-600" />
        {row.currentRole ?? '—'}
      </span>
      {row.currentCompany && (
        <p className="text-[11px] font-semibold text-slate-500">{row.currentCompany}</p>
      )}
    </td>
    <td className="px-3 py-3 text-slate-300">
      {row.totalYearsOfExperience != null ? `${row.totalYearsOfExperience} yrs` : '—'}
    </td>
    <td className="px-3 py-3">
      {row.availabilityStatus ? (
        <span className="rounded-md bg-slate-800 px-2 py-1 text-[10px] font-black uppercase text-slate-300">
          {row.availabilityStatus}
        </span>
      ) : (
        '—'
      )}
    </td>
    <td className="px-3 py-3 text-[11px] font-semibold text-slate-500">
      {formatDate(row.createdAt)}
    </td>
  </tr>
);

const RecruiterRow = ({
  row,
  selected,
  onToggle,
}: {
  row: RecruiterDataRow;
  selected: boolean;
  onToggle: (id: string) => void;
}) => (
  <tr className="border-b border-slate-800/60 hover:bg-slate-800/30">
    <td className="px-3 py-3">
      <input type="checkbox" checked={selected} onChange={() => onToggle(row.id)} />
    </td>
    <td className="px-3 py-3">
      <p className="font-bold text-slate-100">{row.fullName}</p>
      <p className="text-[11px] font-semibold text-slate-500">{row.email ?? '—'}</p>
    </td>
    <td className="px-3 py-3 text-slate-300">
      <span className="inline-flex items-center gap-1.5">
        <Briefcase size={12} className="text-slate-600" />
        {row.designation ?? '—'}
      </span>
      {row.companyAdmin && (
        <span className="ml-2 rounded-md bg-cyan-500/15 px-1.5 py-0.5 text-[9px] font-black uppercase text-cyan-300">
          Admin
        </span>
      )}
    </td>
    <td className="px-3 py-3 text-slate-300">
      <span className="inline-flex items-center gap-1.5">
        <Building2 size={12} className="text-slate-600" />
        {row.companyId ? row.companyId.slice(0, 8) : '—'}
      </span>
    </td>
    <td className="px-3 py-3">
      <span
        className={`rounded-md px-2 py-1 text-[10px] font-black uppercase ${
          row.active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-800 text-slate-400'
        }`}
      >
        {row.active ? 'Active' : 'Inactive'}
      </span>
    </td>
    <td className="px-3 py-3 text-[11px] font-semibold text-slate-500">
      {formatDate(row.createdAt)}
    </td>
  </tr>
);

const Modal = ({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4">
    <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-black uppercase tracking-wider text-slate-200">{title}</h2>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-200">
          <X size={18} />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const StatTile = ({
  icon,
  label,
  value,
  accent = 'cyan',
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  accent?: 'cyan' | 'emerald' | 'amber' | 'sky' | 'violet' | 'rose' | 'fuchsia' | 'indigo' | 'teal';
}) => {
  const ring: Record<string, string> = {
    cyan: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
    emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
    amber: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
    sky: 'border-sky-500/30 bg-sky-500/10 text-sky-300',
    violet: 'border-violet-500/30 bg-violet-500/10 text-violet-300',
    rose: 'border-rose-500/30 bg-rose-500/10 text-rose-300',
    fuchsia: 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300',
    indigo: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-300',
    teal: 'border-teal-500/30 bg-teal-500/10 text-teal-300',
  };
  const bar: Record<string, string> = {
    cyan: 'from-cyan-500 to-teal-500',
    emerald: 'from-emerald-500 to-teal-500',
    amber: 'from-amber-500 to-orange-500',
    sky: 'from-sky-500 to-cyan-500',
    violet: 'from-violet-500 to-fuchsia-500',
    rose: 'from-rose-500 to-pink-500',
    fuchsia: 'from-fuchsia-500 to-pink-500',
    indigo: 'from-indigo-500 to-violet-500',
    teal: 'from-teal-500 to-cyan-500',
  };
  return (
    <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/50">
      <div className={`h-1 w-full bg-gradient-to-r ${bar[accent]}`} />
      <div className="p-3">
        <span className={`mb-2 inline-flex h-8 w-8 items-center justify-center rounded-lg border ${ring[accent]}`}>
          {icon}
        </span>
        <p className="text-2xl font-black text-slate-100">{value.toLocaleString()}</p>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
    </div>
  );
};

const MiniDist = ({
  title,
  values,
  activeValue,
  onPick,
  accent = 'from-cyan-500 to-teal-500',
}: {
  title: string;
  values?: FacetCount[];
  activeValue?: string;
  onPick?: (value: string) => void;
  accent?: string;
}) => {
  const list = values ?? [];
  const max = list.reduce((m, v) => Math.max(m, v.count), 0) || 1;
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
      <p className="mb-2 text-[11px] font-black uppercase tracking-wider text-slate-400">{title}</p>
      {list.length === 0 ? (
        <p className="text-[11px] font-semibold text-slate-600">No data</p>
      ) : (
        <div className="space-y-2">
          {list.slice(0, 5).map((v) => {
            const active = activeValue === v.value;
            return (
              <button
                key={v.value}
                type="button"
                disabled={!onPick}
                onClick={() => onPick?.(v.value)}
                className={`block w-full text-left ${onPick ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div className="mb-0.5 flex items-center justify-between text-[11px]">
                  <span className={`truncate pr-2 font-semibold ${active ? 'text-cyan-300' : 'text-slate-400'}`}>
                    {v.value}
                  </span>
                  <span className="font-black text-slate-300">{v.count}</span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-slate-800">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${accent}`}
                    style={{ width: `${Math.max(6, Math.round((v.count / max) * 100))}%` }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * "Talent Insights": every tracked attribute rendered as a value/count distribution, grouped by
 * category. Clickable bars drill the directory (via the generic field/value filter) so the whole
 * picture can be read at a glance and then narrowed in one click.
 */
const TalentInsights = ({
  analytics,
  loading,
  activeField,
  activeValue,
  activeMin,
  activeMax,
  onPick,
}: {
  analytics: TalentAnalytics | null;
  loading: boolean;
  activeField: string;
  activeValue: string;
  activeMin: string;
  activeMax: string;
  onPick: (section: AnalyticsSection, value: string) => void;
}) => {
  const grouped = useMemo(() => {
    const map = new Map<string, AnalyticsSection[]>();
    (analytics?.sections ?? []).forEach((s) => {
      const arr = map.get(s.category) ?? [];
      arr.push(s);
      map.set(s.category, arr);
    });
    return Array.from(map.entries())
      .map(([category, sections]) => ({ category, sections }))
      .sort((a, b) => {
        const ia = CATEGORY_ORDER.indexOf(a.category);
        const ib = CATEGORY_ORDER.indexOf(b.category);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
  }, [analytics]);

  const isActive = (section: AnalyticsSection, value: string): boolean => {
    if (section.key === 'experience') {
      const bucket = parseExperienceBucket(value);
      return (
        (bucket.min !== '' || bucket.max !== '') &&
        bucket.min === activeMin &&
        bucket.max === activeMax
      );
    }
    return !!section.filterField && section.filterField === activeField && activeValue === value;
  };

  return (
    <section className="rounded-2xl border border-t-2 border-slate-800 border-t-violet-500/50 bg-slate-900/60 p-4">
      <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-400">
        <BarChart3 size={14} className="text-violet-400" /> Talent Insights
        <span className="ml-auto text-[10px] font-bold normal-case text-slate-600">
          {analytics
            ? `${analytics.total.toLocaleString()} in scope · every tracked field`
            : 'scoped to current filters'}
        </span>
      </div>

      {loading && !analytics ? (
        <div className="py-8 text-center">
          <Loader2 className="mx-auto animate-spin text-slate-500" size={20} />
        </div>
      ) : grouped.length === 0 ? (
        <p className="text-[11px] font-semibold text-slate-600">No analytics available.</p>
      ) : (
        <div className="space-y-5">
          {grouped.map(({ category, sections }, ci) => (
            <div key={category}>
              <p
                className={`mb-2 text-[10px] font-black uppercase tracking-[0.18em] ${
                  CATEGORY_COLORS[ci % CATEGORY_COLORS.length]
                }`}
              >
                {category}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {sections.map((section) => {
                  const list = section.values.slice(0, 6);
                  const max = section.values.reduce((m, v) => Math.max(m, v.count), 0) || 1;
                  const clickableFor = (value: string) =>
                    !UNKNOWN_BUCKETS.has(value) &&
                    (section.key === 'experience' || !!section.filterField);
                  return (
                    <div
                      key={section.key}
                      className={`rounded-xl border border-l-2 border-slate-800 bg-slate-950/50 p-3 ${
                        CATEGORY_BORDERS[ci % CATEGORY_BORDERS.length]
                      }`}
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="truncate text-[11px] font-black uppercase tracking-wider text-slate-400">
                          {section.label}
                        </p>
                        <span className="shrink-0 rounded-md bg-slate-800 px-1.5 py-0.5 text-[10px] font-black text-slate-300">
                          {section.total.toLocaleString()}
                        </span>
                      </div>
                      {list.length === 0 ? (
                        <p className="text-[11px] font-semibold text-slate-600">No data</p>
                      ) : (
                        <div className="space-y-1.5">
                          {list.map((v) => {
                            const active = isActive(section, v.value);
                            const bar = (
                              <>
                                <div className="mb-0.5 flex items-center justify-between gap-2 text-[11px]">
                                  <span
                                    className={`truncate pr-2 font-semibold ${
                                      active ? 'text-cyan-300' : 'text-slate-400'
                                    }`}
                                  >
                                    {v.value}
                                  </span>
                                  <span className="shrink-0 font-black text-slate-300">
                                    {v.count.toLocaleString()}
                                  </span>
                                </div>
                                <div className="h-1 w-full overflow-hidden rounded-full bg-slate-800">
                                  <div
                                    className={`h-full rounded-full ${
                                      active
                                        ? 'bg-cyan-400'
                                        : 'bg-gradient-to-r from-cyan-500 to-teal-500'
                                    }`}
                                    style={{ width: `${Math.max(5, Math.round((v.count / max) * 100))}%` }}
                                  />
                                </div>
                              </>
                            );
                            return clickableFor(v.value) ? (
                              <button
                                key={v.value}
                                type="button"
                                onClick={() => onPick(section, v.value)}
                                className="block w-full cursor-pointer text-left"
                                title={`Filter by ${section.label}: ${v.value}`}
                              >
                                {bar}
                              </button>
                            ) : (
                              <div key={v.value} className="cursor-default">
                                {bar}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default CandidatesDataComponent;
